import React, { useEffect, useState } from 'react';
import ProposalStatusLayout from 'components/common/proposal/ProposalStatusLayout';
import { observer } from 'mobx-react-lite';
import { useRootStore } from 'store/rootStore';
import { useHistory, useParams } from 'react-router-dom';
import EditableElectionsContent from 'components/election/EditableElectionsContent';
import ElectionStatistics from 'components/election/ElectionsStatistics';
import { CARD_DATA } from 'global/checklists-helpers/ChecklistHelper';
import { Button, PageHeader, useQueryParams } from '@insurancemenu/imenu-components-lib';
import { CloudUpload } from '@insurancemenu/imenu-components-lib/dist/icons';
import { PageWrapperStyled } from '../../globalStyles';
import { ButtonWrapperStyled } from './styles';

const ElectionsPage: React.FC = observer(() => {
  const { periodId } = useParams<{ periodId: string }>();
  const query = useQueryParams();
  const groupId = query.get('groupId') || '';
  const history = useHistory();

  // The initial loading state is crucial for ensuring a smooth display of the spinner.
  // checkElectionForCompleted and getSubscribersList are responsible for setting the loading state separately within the store
  // and are utilized across various other pages.
  const [initialLoading, setInitialLoading] = useState(false);

  const {
    employeeElectionsStore: {
      loading,
      filter,
      sortOrder,
      sortBy,
      searchValue,
      statusesFilter,
      classesFilter,
      currentPage,
      resetInitialData,
      getSubscribersList,
      checkElectionForCompleted,
      updateElectionStatus,
      isElectionCompleted,
    },
    subscriberStore: { isPeriodNotEditable },
  } = useRootStore();

  const getSubscribersPage = async (): Promise<void> => {
    await getSubscribersList(
      groupId,
      periodId,
      sortBy,
      sortOrder,
      filter,
      searchValue,
      statusesFilter,
      classesFilter,
      currentPage,
    );
  };

  useEffect(() => {
    setInitialLoading(true);
    void checkElectionForCompleted(periodId).then(async () => {
      await getSubscribersPage();
      setInitialLoading(false);
    });

    return (): void => {
      resetInitialData();
      updateElectionStatus(null);
    };
  }, []);

  const onBackRedirect = (): void => {
    history.push(`/checklist/${periodId}`);
  };

  return (
    <ProposalStatusLayout>
      <PageWrapperStyled>
        <PageHeader
          title={CARD_DATA.EMPLOYEE_ELECTIONS.title}
          subtitle={CARD_DATA.EMPLOYEE_ELECTIONS.text}
          onBackButtonClick={onBackRedirect}
          spacing={!isPeriodNotEditable && !isElectionCompleted ? 'withActionsBelow' : 'default'}
        />
        <ButtonWrapperStyled>
          <Button
            size="large"
            color="primary"
            disabled={!!(loading || isPeriodNotEditable || isElectionCompleted)}
            leftIconProp={<CloudUpload />}
            onClick={(): void => {
              history.push(`/manage-add-employees/${groupId}/${periodId}`);
            }}
          >
            Add More Employees
          </Button>
        </ButtonWrapperStyled>
        <ElectionStatistics />
        <EditableElectionsContent initialLoading={initialLoading} />
      </PageWrapperStyled>
    </ProposalStatusLayout>
  );
});

export default ElectionsPage;
